// components/public/MidArticleCta.tsx
// Reusable mid-article CTA box for long guides (Priority 240). Props-driven
// rather than a one-off hardcoded product block, so a guide can point this at
// a live affiliate hop, an internal category hub, or the on-page Playbook
// signup without a new component per guide or per product.
//
// `variant` picks the link contract, not just the copy:
//   - "hop":      outbound affiliate link. Reuses the AffiliateCtaButton
//                 contract (target=_blank, rel="sponsored nofollow noopener noreferrer",
//                 hrefLang, aria-label mirroring visible text) documented in
//                 app/(public)/reviews/[slug]/page.tsx and already mirrored by
//                 DiyTaxComparison and StickyMobileCta — a fourth hand-copy of
//                 the same five attributes was the wrong move, not a good one.
//   - "hub":      internal navigation to a category/hub page. Plain next/link,
//                 no target=_blank — leaving the article is not opening a new
//                 tab for an internal destination.
//   - "playbook":  internal navigation toward the on-page lead-capture form
//                 (e.g. an in-page anchor). Same internal-link contract as "hub".
//
// Do not invent products or hops here — the guide data in lib/guides.ts is
// the only place variant/href/copy get decided, so this component never
// needs to change to point at a different product or a different live hop.
//
// "use client" (Priority 247): the "hop" variant beacons cta_click pre-
// navigation, same contract as AffiliateCtaButton/StickyMobileCta (see
// lib/tracking/client-track.ts). "hub"/"playbook" are unaffected — no new
// tracking added there, out of scope for #247.
//
// `sell` (Priority 304) picks the tone, not the link contract: "hard" is the
// filled button, "soft" is the same label as a text link (no fill, no shadow,
// no scale hover). Omitted, it takes the variant default from
// lib/guide-cta-sell.ts — hop hard, hub/playbook soft. Everything `variant`
// decides above (target/rel, next/link, the cta_click beacon) is the same
// for both tones.

"use client";

import Link from "next/link";
import { ArrowRight, ExternalLink } from "lucide-react";
import GoPrefetchAnchor from "@/components/public/GoPrefetchAnchor";
import { trackCtaClick } from "@/lib/tracking/client-track";
import { resolveGuideCtaSell, type GuideCtaSell } from "@/lib/guide-cta-sell";

export type MidArticleCtaVariant = "hop" | "playbook" | "hub";

export interface MidArticleCtaProps {
  variant: MidArticleCtaVariant;
  /** Outbound `/go/<slug>` path for "hop", internal path or anchor otherwise. */
  href: string;
  title: string;
  body: string;
  ctaLabel: string;
  /** Tone: "hard" filled button, "soft" text link. Defaults per variant. */
  sell?: GuideCtaSell;
}

const HARD_CLASS =
  "group inline-flex min-h-12 items-center gap-2 rounded-lg bg-forge-trust px-5 py-2.5 text-base font-bold text-white transition-all hover:bg-forge-trust-hover";
const SOFT_CLASS =
  "group inline-flex items-center gap-1.5 text-base font-semibold text-forge-trust underline underline-offset-2 transition-colors hover:text-forge-trust-hover";

export default function MidArticleCta({ variant, href, title, body, ctaLabel, sell }: MidArticleCtaProps) {
  const isHop = variant === "hop";
  const ariaLabel = isHop ? `${ctaLabel} (opens in new tab)` : ctaLabel;
  const linkClass = resolveGuideCtaSell(variant, sell) === "soft" ? SOFT_CLASS : HARD_CLASS;

  return (
    <section
      aria-label={title}
      className="my-10 rounded-xl border border-content-border bg-content-surface p-6 sm:p-8"
    >
      <h2 className="text-lg font-bold text-content-text mb-2">{title}</h2>
      <p className="text-base text-content-muted leading-relaxed mb-5">{body}</p>

      {isHop ? (
        <GoPrefetchAnchor
          href={href}
          target="_blank"
          rel="sponsored nofollow noopener noreferrer"
          hrefLang="en"
          aria-label={ariaLabel}
          onClick={() => trackCtaClick({ linkUrl: href, ctaPlacement: "mid-article-hop" })}
          className={linkClass}
        >
          {ctaLabel}
          <ExternalLink size={14} className="transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
        </GoPrefetchAnchor>
      ) : (
        <Link
          href={href}
          aria-label={ariaLabel}
          className={linkClass}
        >
          {ctaLabel}
          <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
        </Link>
      )}
    </section>
  );
}
